/**
 * Quais volumes vão para o papel — escritos como quem fala.
 *
 * <p>A tela de etiquetas perguntava <b>quantas</b> e imprimia as
 * <b>primeiras N</b>: pedir a etiqueta do tambor 17 emitia dezessete adesivos,
 * e com eles <b>dezesseis emissões que ninguém pediu</b> — registro imutável
 * por gatilho de banco (V11), que a reconciliação de rótulos do Art. 200 passa
 * a contar como material aplicado. Este arquivo é a outra ponta: a pessoa
 * escreve <code>17</code>, <code>3-7</code> ou <code>1, 5, 9-12</code>, e o que
 * sai é exatamente isso.
 *
 * <p>Ele é <b>lógica de domínio da tela</b>, não componente: mora ao lado de
 * {@code situacaoDoLote.ts} pelo mesmo motivo — é uma regra de leitura desta
 * área, com teste próprio, e componente visual continua nascendo só em
 * {@code packages/ui}.
 *
 * <h2>O que ele recusa, e por que recusar é o comportamento certo</h2>
 *
 * <p>Uma expressão que o sistema não entende <b>não muda a escolha</b>. A tela
 * mantém o que estava marcado e diz o que não entendeu — porque a alternativa
 * (adivinhar) escolheria tambor por conta própria numa tela que emite documento
 * regulado.
 */

/** O que a expressão digitada quer dizer — ou por que ela não diz nada. */
export interface Escolha {
  /** Ordenados e sem repetição. Vazio quando a expressão está vazia. */
  numeros: number[];
  /** `null` quando a expressão foi entendida inteira. */
  erro: string | null;
}

/**
 * Lê `17`, `3-7`, `1, 5, 9-12` — e recusa o resto.
 *
 * <p><b>Repetição some.</b> `3-7, 5` são cinco volumes, não seis: o servidor
 * recusa o mesmo volume duas vezes no mesmo pedido (pode ser clique duplo, e
 * uma reimpressão que ninguém pediu é rastro falso), e a tela não tem por que
 * construir um pedido que ela sabe que será recusado.
 *
 * <p><b>Faixa invertida é a mesma faixa.</b> `7-3` e `3-7` selecionam os mesmos
 * cinco tambores — a ordem em que se digita não muda quais adesivos saem, e
 * recusar aqui seria inventar um erro que não tem consequência física.
 *
 * <p><b>`3-` é faixa em construção, não erro.</b> Quem digita `3-7` passa por
 * ela; marcar erro no meio da digitação ensina a ignorar o erro que aparece no
 * fim. Enquanto o segundo número não chega, vale só o primeiro.
 *
 * @param total quantos recipientes o lote tem — o teto, vindo do servidor.
 */
export function interpretarEscolha(texto: string, total: number): Escolha {
  const numeros = new Set<number>();

  for (const bruto of texto.split(/[,;]/)) {
    const item = bruto.trim();
    if (item === "") continue;

    const faixa = /^(\d+)\s*-\s*(\d*)$/.exec(item);
    const unico = /^(\d+)$/.exec(item);

    if (unico !== null) {
      const n = Number(unico[1]);
      const recusa = foraDoLote(n, total);
      if (recusa !== null) return { numeros: [], erro: recusa };
      numeros.add(n);
      continue;
    }

    if (faixa !== null) {
      const inicio = Number(faixa[1]);
      const recusaInicio = foraDoLote(inicio, total);
      if (recusaInicio !== null) return { numeros: [], erro: recusaInicio };

      // Faixa ainda sendo digitada (`3-`): vale o que já se sabe.
      if (faixa[2] === "") {
        numeros.add(inicio);
        continue;
      }

      const fim = Number(faixa[2]);
      const recusaFim = foraDoLote(fim, total);
      if (recusaFim !== null) return { numeros: [], erro: recusaFim };

      for (let n = Math.min(inicio, fim); n <= Math.max(inicio, fim); n += 1) {
        numeros.add(n);
      }
      continue;
    }

    return {
      numeros: [],
      erro: `não entendi «${item}» — escreva números, como 17, 3-7 ou 1, 5, 9-12.`,
    };
  }

  return { numeros: [...numeros].sort((a, b) => a - b), erro: null };
}

/**
 * A mensagem é a **do servidor**, palavra por palavra.
 *
 * <p>`o volume 99 não existe neste lote — ele tem 20 recipiente(s)` é o que o
 * caso de uso responde. Duas redações para a mesma recusa fazem a pessoa achar
 * que são duas coisas diferentes.
 */
function foraDoLote(numero: number, total: number): string | null {
  if (numero >= 1 && numero <= total) return null;
  return `o volume ${numero} não existe neste lote — ele tem ${total} recipiente(s).`;
}

/**
 * O caminho de volta: da escolha para a expressão.
 *
 * <p>Quem toca nos números da grade também vê o campo mudar — os dois são a
 * mesma escolha, e um campo que só serve de entrada vira mentira assim que
 * alguém toca num tambor pelo dedo.
 *
 * <p>Ele **compacta**: `1, 2, 3, 7` vira `1-3, 7`. Vinte tambores escritos um a
 * um não se conferem de relance, e conferir de relance é o que a pessoa faz
 * antes de clicar em imprimir.
 */
export function escreverEscolha(numeros: readonly number[]): string {
  const ordenados = [...new Set(numeros)].sort((a, b) => a - b);
  const trechos: string[] = [];

  let inicio: number | null = null;
  let anterior: number | null = null;

  const fechar = () => {
    if (inicio === null || anterior === null) return;
    if (anterior - inicio >= 2) trechos.push(`${inicio}-${anterior}`);
    else for (let n = inicio; n <= anterior; n += 1) trechos.push(String(n));
    // Uma faixa de DOIS sai como dois números: `3-4` não é mais curto que
    // `3, 4`, e é mais difícil de ler.
  };

  for (const n of ordenados) {
    if (anterior !== null && n === anterior + 1) {
      anterior = n;
      continue;
    }
    fechar();
    inicio = n;
    anterior = n;
  }
  fechar();

  return trechos.join(", ");
}
